import { create } from 'zustand';
import { IChatMessage, ChatStatus } from '@/types/ai-chat.types';

interface CompanySession {
  messages: IChatMessage[];
  sessionId: string;
}

interface AIChatStore {
  messages: IChatMessage[];
  status: ChatStatus;
  isStreaming: boolean;
  streamingMessageId: string | null;
  sessionId: string;
  isPanelOpen: boolean;
  companySessions: Record<string, CompanySession>;

  addMessage: (message: IChatMessage) => void;
  updateMessage: (id: string, updates: Partial<IChatMessage>) => void;
  setMessages: (messages: IChatMessage[]) => void;
  clearMessages: () => void;
  setStatus: (status: ChatStatus) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  setStreamingMessageId: (id: string | null) => void;
  setIsPanelOpen: (open: boolean) => void;
  setSessionId: (sessionId: string) => void;
  saveCompanySession: (companyId: string) => void;
  restoreCompanySession: (companyId: string) => void;
}

export const useAIChatStore = create<AIChatStore>((set, get) => ({
  messages: [],
  status: ChatStatus.IDLE,
  isStreaming: false,
  streamingMessageId: null,
  sessionId: crypto.randomUUID(),
  isPanelOpen: false,
  companySessions: {},

  addMessage: (message) =>
    set((state) => ({ messages: [...state.messages, message] })),

  updateMessage: (id, updates) =>
    set((state) => ({
      messages: state.messages.map((m) => (m.id === id ? { ...m, ...updates } : m)),
    })),

  setMessages: (messages) => set({ messages }),

  clearMessages: () => set({ messages: [], status: ChatStatus.IDLE }),

  setStatus: (status) => set({ status }),

  setIsStreaming: (isStreaming) => set({ isStreaming }),

  setStreamingMessageId: (id) => set({ streamingMessageId: id }),

  setIsPanelOpen: (open) => set({ isPanelOpen: open }),

  setSessionId: (sessionId) => set({ sessionId }),

  saveCompanySession: (companyId) => {
    const { messages, sessionId, companySessions } = get();
    set({
      companySessions: {
        ...companySessions,
        [companyId]: { messages, sessionId },
      },
    });
  },

  restoreCompanySession: (companyId) => {
    const { companySessions } = get();
    const saved = companySessions[companyId];
    if (saved) {
      set({ messages: saved.messages, sessionId: saved.sessionId, status: ChatStatus.IDLE });
    } else {
      set({ messages: [], sessionId: crypto.randomUUID(), status: ChatStatus.IDLE });
    }
  },
}));
